import React, { useState, useEffect } from "react";
import styled from "styled-components";
import CustomModal from "../Generic/CustomModal";
import Pill from "../Generic/Pill";
import Tooltip from "../Generic/Tooltip";
import StyledTable, { TableBody, TableCell, TableHead, TableHeader, TableRow } from "../Generic/StyledTable";
import { getWebhookDeliveriesAPICall, resendWebhookDeliveryAPICall } from "../../api/webhooks/webhookService";
import { showErrorNotification, showGenericError, showSuccessNotification } from "../../helper/Notifications";
import { formatDate } from "../../helper/dateFormat";

const PER_PAGE = 20;
const STATUS_VARIANT = { pending: "warning", succeeded: "success", failed: "error" };

const WebhookDeliveriesModal = ({ projectId, endpoint, onClose }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadDeliveries = () => {
    getWebhookDeliveriesAPICall(
      projectId,
      endpoint.id,
      page,
      PER_PAGE,
      (response) => {
        setDeliveries(response.data.webhook_deliveries);
        setTotalPages(response.data.pagination?.total_pages || 1);
      },
      showGenericError
    );
  };

  useEffect(() => {
    if (endpoint) loadDeliveries();
  }, [endpoint?.id, page]);

  const handleResend = (delivery) => {
    resendWebhookDeliveryAPICall(
      projectId,
      delivery.id,
      () => {
        showSuccessNotification("Delivery queued");
        loadDeliveries();
      },
      (error) => error?.response?.data?.message ? showErrorNotification(error.response.data.message) : showGenericError()
    );
  };

  return (
    <CustomModal
      isOpen={!!endpoint}
      onOpenChange={onClose}
      title="Webhook Deliveries"
      subtitle={endpoint?.url}
      titleAlignment="left"
      showCloseButton={true}
      width={900}
      buttons={[{ text: "Close", onClick: onClose, variant: "secondary" }]}
    >
      <StyledTable>
        <TableHeader>
          <TableRow>
            <TableHead style={{ textAlign: "left" }}>Event</TableHead>
            <TableHead style={{ textAlign: "center" }}>Status</TableHead>
            <TableHead style={{ textAlign: "center" }}>Response</TableHead>
            <TableHead style={{ textAlign: "center" }}>Attempts</TableHead>
            <TableHead style={{ textAlign: "center" }}>Created</TableHead>
            <TableHead style={{ textAlign: "center" }}>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deliveries.length > 0 ? (
            deliveries.map((delivery) => (
              <TableRow key={delivery.id}>
                <TableCell>
                  <Mono data-emotii-exclude>{delivery.event_type}</Mono>
                </TableCell>
                <TableCell style={{ textAlign: "center" }}>
                  <Pill variant={STATUS_VARIANT[delivery.status]} size="sm" className="capitalize">{delivery.status}</Pill>
                </TableCell>
                <TableCell style={{ textAlign: "center" }}>
                  {delivery.error ? (
                    <Tooltip content={delivery.error} side="top">
                      <MutedText>{delivery.response_code || "Error"}</MutedText>
                    </Tooltip>
                  ) : (
                    <MutedText>{delivery.response_code || "-"}</MutedText>
                  )}
                </TableCell>
                <TableCell style={{ textAlign: "center" }}>
                  <MutedText>{delivery.attempts}</MutedText>
                </TableCell>
                <TableCell style={{ textAlign: "center" }}>
                  <MutedText>{formatDate(delivery.created_at)}</MutedText>
                </TableCell>
                <TableCell style={{ textAlign: "center" }}>
                  {delivery.status !== "pending" && (
                    <LinkButton onClick={() => handleResend(delivery)}>Resend</LinkButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6}>
                <EmptyText>No deliveries yet. Send a test event to try the endpoint.</EmptyText>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </StyledTable>

      {totalPages > 1 && (
        <Pagination>
          <LinkButton disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</LinkButton>
          <MutedText>Page {page} of {totalPages}</MutedText>
          <LinkButton disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</LinkButton>
        </Pagination>
      )}
    </CustomModal>
  );
};

export default WebhookDeliveriesModal;

const Mono = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #111827;
`;

const MutedText = styled.span`
  color: #667085;
  font-size: 14px;
  white-space: nowrap;
`;

const EmptyText = styled.div`
  color: #667085;
  font-size: 14px;
  text-align: center;
  padding: 32px 20px;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #7f56d9;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    color: #d0d5dd;
    cursor: default;
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 12px;
`;
